/**
 * Recognising links: what the composer's Link field accepts, what its
 * autolinker and paste handler turn into a link, and what the plain-text
 * reading pane makes clickable.
 *
 * Every `href` produced here is one `safeHref` in `./composeHtml` keeps, and
 * that is checked on the way out rather than assumed: the outbound allowlist
 * is the policy, and this module only ever *narrows* it. The detectors
 * recognise three shapes — `http(s)://…`, `www.…` and an email address — and
 * build the target from the shape, so no amount of cleverness in the typed or
 * pasted text can make one of them return a `javascript:` or `data:` URL. The
 * Link field takes what the user typed at their word and so has to *refuse*
 * the rest, which it does with a reason rather than a silent drop.
 *
 * Deliberately not recognised by the autolinker: a bare `example.com`. Gmail
 * does it with a list of top-level domains; without one, `file.txt`,
 * `e.g.` and `node.js` all become links, and a link nobody asked for is harder
 * to notice than one that did not happen. Typed into the Link field, where the
 * user has said it is an address, a bare domain is accepted as `https://`.
 */

import { safeHref } from './composeHtml';

/** The Link field's verdict: a target to use, or why there is none. */
export type LinkInput = { href: string } | { problem: string };

/** A link found in text: `text.slice(start, end)` is what gets linked. */
export interface FoundLink {
  start: number;
  end: number;
  href: string;
}

const ALLOWED = 'Use a web address (https://…) or an email address.';

/** `scheme:` at the start of a string, as a URL parser would read it. */
const SCHEME = /^([a-z][a-z0-9+.-]*):/i;

/**
 * A host with an optional port and path, and no scheme: `example.com`,
 * `www.example.co.uk:8080/x?y`. Letters are Unicode letters so an
 * internationalised domain typed as itself is accepted; `new URL` converts it.
 */
const BARE_HOST =
  /^(?:[\p{L}\p{N}](?:[\p{L}\p{N}-]*[\p{L}\p{N}])?\.)+\p{L}{2,}(?::\d{1,5})?(?:[/?#]\S*)?$/u;

/** An email address, loosely: local part, `@`, a dotted domain. */
const EMAIL = /^[\p{L}\p{N}._%+-]+@(?:[\p{L}\p{N}](?:[\p{L}\p{N}-]*[\p{L}\p{N}])?\.)+\p{L}{2,}$/u;

/**
 * What the Link field makes of what was typed into it.
 *
 * A scheme the allowlist admits is taken as written; an email address becomes
 * `mailto:`; a bare host becomes `https://`. Any other scheme is refused by
 * name — "javascript: links can't be sent" says what happened, where "invalid
 * link" would leave the user guessing — and anything else is refused as not
 * an address at all.
 */
export function linkFromInput(raw: string): LinkInput {
  // Stripped the way `safeHref` strips it, so `java\tscript:` is read as the
  // `javascript:` a mail client would resolve it to, and refused as that.
  const typed = raw.replaceAll('\u0000', '').replace(/[\t\n\r]/g, '').trim();
  if (typed === '') return { problem: `Enter a link address. ${ALLOWED}` };

  const accept = (href: string): LinkInput =>
    safeHref(href) === href && wellFormed(href) ? { href } : { problem: `That doesn't look like a link address. ${ALLOWED}` };

  if (EMAIL.test(typed)) return accept(`mailto:${typed}`);
  if (BARE_HOST.test(typed)) return accept(`https://${typed}`);

  const scheme = SCHEME.exec(typed)?.[1];
  if (scheme !== undefined) {
    if (/^(?:https?|mailto|tel)$/i.test(scheme)) return accept(typed);
    return { problem: `${scheme.toLowerCase()}: links can't be sent. ${ALLOWED}` };
  }
  return { problem: `That doesn't look like a link address. ${ALLOWED}` };
}

/**
 * The checks the allowlist does not make but a link someone clicks needs: no
 * spaces, a host for http(s), an address for mailto. `safeHref` answers "may
 * this be sent"; this answers "is it a link at all".
 */
function wellFormed(href: string): boolean {
  if (/\s/.test(href)) return false;
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return false;
  }
  if (url.protocol === 'http:' || url.protocol === 'https:') return url.hostname !== '';
  return url.pathname !== '';
}

/**
 * Characters that end a sentence rather than a URL. Stripped from the end of
 * a detected link one at a time, so "(see https://x.example/a)." keeps the
 * link and leaves ")." as the prose it is.
 */
const TRAILING = /[.,;:!?'"’”>\]}*_]$/;
const LEADING = /^[(<["'‘“*_]/;

/**
 * The link in one whitespace-free word, if there is one: its span within the
 * word and its target. `undefined` when the word is not one of the three
 * shapes, or when its target would not survive the outbound allowlist.
 */
export function detectLink(word: string): FoundLink | undefined {
  let start = 0;
  let end = word.length;
  while (start < end && LEADING.test(word[start]!)) start += 1;
  for (;;) {
    const text = word.slice(start, end);
    if (TRAILING.test(text)) {
      end -= 1;
      continue;
    }
    // A closing parenthesis is the URL's own when it balances one inside it:
    // Wikipedia's `Foo_(bar)`. Otherwise it closes the sentence's bracket.
    if (text.endsWith(')') && count(text, '(') < count(text, ')')) {
      end -= 1;
      continue;
    }
    break;
  }
  const text = word.slice(start, end);
  const href = targetOf(text);
  if (href === undefined || safeHref(href) !== href || !wellFormed(href)) return undefined;
  return { start, end, href };
}

function targetOf(text: string): string | undefined {
  if (/^https?:\/\/[^\s<>"]+$/i.test(text)) return text;
  if (/^www\.[^\s<>"./]+\.[^\s<>"]+$/i.test(text)) return `https://${text}`;
  if (/^mailto:/i.test(text) && EMAIL.test(text.slice(7))) return text;
  if (EMAIL.test(text)) return `mailto:${text}`;
  return undefined;
}

function count(text: string, char: string): number {
  return text.split(char).length - 1;
}

/** Every link in `text`, in order, with offsets into `text`. */
export function findLinks(text: string): FoundLink[] {
  const found: FoundLink[] = [];
  for (const match of text.matchAll(/\S+/g)) {
    const link = detectLink(match[0]);
    if (link === undefined) continue;
    found.push({ start: match.index + link.start, end: match.index + link.end, href: link.href });
  }
  return found;
}
